/* Internal WebRTC Intercom — pure browser-to-browser audio between CMS users.
   No SIP, no Asterisk — signalling via /api/ws/intercom, media via RTCPeerConnection. */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import {
    Phone, PhoneSlash, MicrophoneSlash, Microphone, UserCircle,
    CheckCircle, SpinnerGap, XCircle, Broadcast, ArrowsClockwise,
} from '@phosphor-icons/react';

const ICE_SERVERS = [{ urls: 'stun:stun.l.google.com:19302' }];

const PhaseLabel = {
    idle: 'Připraveno',
    connecting: 'Připojuji…',
    online: 'Online',
    offline: 'Odpojeno',
    dialing: 'Vyzvání…',
    incoming: 'Příchozí hovor',
    in_call: 'Hovor',
};

const Intercom = () => {
    const [me, setMe] = useState(null);
    const [online, setOnline] = useState([]);          // [{user_id, email, name, extension}]
    const [directory, setDirectory] = useState([]);    // all users
    const [wsPhase, setWsPhase] = useState('connecting');
    const [callPhase, setCallPhase] = useState('idle');
    const [peer, setPeer] = useState(null);            // active peer info
    const [muted, setMuted] = useState(false);
    const [duration, setDuration] = useState(0);

    const wsRef = useRef(null);
    const pcRef = useRef(null);
    const localStreamRef = useRef(null);
    const remoteAudioRef = useRef(null);
    const callIdRef = useRef(null);
    const peerIdRef = useRef(null);
    const ringtoneRef = useRef(null);
    const dialtoneRef = useRef(null);
    const durTimer = useRef(null);
    const pendingIceRef = useRef([]);
    const recorderRef = useRef(null);
    const recordChunksRef = useRef([]);
    const recordStartRef = useRef(null);

    const refreshDirectory = useCallback(() => {
        api.get('/intercom/directory').then((r) => setDirectory(r.data.users || []));
    }, []);

    // ---- WebSocket connect/reconnect ----
    const connectWs = useCallback(() => {
        const token = localStorage.getItem('vg_token');
        if (!token) return;
        const backend = process.env.REACT_APP_BACKEND_URL || '';
        const proto = backend.startsWith('https') ? 'wss' : 'ws';
        const host = backend.replace(/^https?:\/\//, '');
        const url = `${proto}://${host}/api/ws/intercom?token=${encodeURIComponent(token)}`;
        const ws = new WebSocket(url);
        wsRef.current = ws;
        setWsPhase('connecting');
        ws.onopen = () => setWsPhase('online');
        ws.onclose = () => {
            setWsPhase('offline');
            setTimeout(() => { if (wsRef.current === ws) connectWs(); }, 2500);
        };
        ws.onerror = () => setWsPhase('offline');
        ws.onmessage = (e) => { try { handleSignal(JSON.parse(e.data)); } catch (_) {} };
    }, []);  // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { refreshDirectory(); connectWs(); }, [refreshDirectory, connectWs]);
    useEffect(() => () => {
        try { wsRef.current?.close(); } catch (_) {}
        try { pcRef.current?.close(); } catch (_) {}
        if (localStreamRef.current) localStreamRef.current.getTracks().forEach((t) => t.stop());
    }, []);

    const sendSignal = (msg) => {
        const ws = wsRef.current;
        if (ws && ws.readyState === 1) ws.send(JSON.stringify(msg));
    };

    const playRingtone = (kind) => {
        const ref = kind === 'dial' ? dialtoneRef : ringtoneRef;
        if (ref.current) { try { ref.current.currentTime = 0; ref.current.play().catch(() => {}); } catch (_) {} }
    };
    const stopRingtones = () => {
        [ringtoneRef, dialtoneRef].forEach((r) => {
            try { if (r.current) { r.current.pause(); r.current.currentTime = 0; } } catch (_) {}
        });
    };

    // ---- WebRTC primitives ----
    const buildPc = useCallback((otherId) => {
        const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
        pcRef.current = pc;
        pc.onicecandidate = (ev) => {
            if (ev.candidate) sendSignal({ type: 'ice', to: otherId, candidate: ev.candidate });
        };
        pc.ontrack = (ev) => {
            if (remoteAudioRef.current) {
                remoteAudioRef.current.srcObject = ev.streams[0];
                remoteAudioRef.current.play().catch(() => {});
            }
        };
        pc.onconnectionstatechange = () => {
            if (['failed', 'disconnected', 'closed'].includes(pc.connectionState)) {
                endCall(true);
            }
        };
        return pc;
    }, []);  // eslint-disable-line react-hooks/exhaustive-deps

    const attachLocalMedia = async (pc) => {
        if (!localStreamRef.current) {
            localStreamRef.current = await navigator.mediaDevices.getUserMedia({
                audio: true, video: false,
            });
        }
        localStreamRef.current.getTracks().forEach((t) => pc.addTrack(t, localStreamRef.current));
    };

    const startCallTimer = () => {
        setDuration(0);
        durTimer.current = setInterval(() => setDuration((d) => d + 1), 1000);
        // Start MediaRecorder mixing local + remote audio
        try {
            const AC = window.AudioContext || window.webkitAudioContext;
            if (!AC) return;
            const ac = new AC();
            const dest = ac.createMediaStreamDestination();
            if (localStreamRef.current) {
                ac.createMediaStreamSource(localStreamRef.current).connect(dest);
            }
            const remoteStream = remoteAudioRef.current?.srcObject;
            if (remoteStream) {
                ac.createMediaStreamSource(remoteStream).connect(dest);
            }
            const rec = new MediaRecorder(dest.stream, { mimeType: 'audio/webm;codecs=opus' });
            recordChunksRef.current = [];
            recordStartRef.current = Date.now();
            rec.ondataavailable = (ev) => {
                if (ev.data && ev.data.size > 0) recordChunksRef.current.push(ev.data);
            };
            rec.start(1000);
            recorderRef.current = rec;
        } catch (e) {
            console.warn('MediaRecorder not available:', e);
        }
    };

    const finalizeRecording = async () => {
        const rec = recorderRef.current;
        if (!rec) return;
        try {
            await new Promise((resolve) => {
                rec.onstop = resolve;
                rec.stop();
            });
            const blob = new Blob(recordChunksRef.current, { type: 'audio/webm' });
            recorderRef.current = null;
            recordChunksRef.current = [];
            if (blob.size < 1024) return;  // ignore micro-blobs
            const dur = Math.round((Date.now() - (recordStartRef.current || Date.now())) / 1000);
            const fd = new FormData();
            fd.append('file', blob, `intercom-${callIdRef.current || Date.now()}.webm`);
            fd.append('direction', 'intercom');
            if (peerIdRef.current) fd.append('peer_user_id', peerIdRef.current);
            fd.append('duration_seconds', String(dur));
            try {
                await api.post('/recordings/upload', fd, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });
                toast.success(`Nahrávka uložena (${dur}s)`);
            } catch (e) {
                console.warn('Upload failed:', e);
            }
        } catch (_) {}
    };

    const endCall = useCallback((silent = false) => {
        const otherId = peerIdRef.current;
        if (otherId && wsRef.current?.readyState === 1) {
            sendSignal({ type: 'bye', to: otherId, call_id: callIdRef.current });
        }
        // Stop recording & upload (fire-and-forget)
        if (recorderRef.current) finalizeRecording();
        try { pcRef.current?.close(); } catch (_) {}
        pcRef.current = null;
        stopRingtones();
        if (durTimer.current) clearInterval(durTimer.current);
        durTimer.current = null;
        if (localStreamRef.current) {
            localStreamRef.current.getTracks().forEach((t) => t.stop());
            localStreamRef.current = null;
        }
        setCallPhase('idle');
        setPeer(null);
        setMuted(false);
        setDuration(0);
        peerIdRef.current = null;
        callIdRef.current = null;
        pendingIceRef.current = [];
        if (!silent) toast.info('Hovor ukončen');
    }, []);

    // ---- Outbound call ----
    const placeCall = useCallback(async (target) => {
        if (callPhase !== 'idle') return;
        try {
            const callId = Math.random().toString(36).slice(2, 12);
            callIdRef.current = callId;
            peerIdRef.current = target.user_id;
            setPeer(target);
            setCallPhase('dialing');
            playRingtone('dial');
            const pc = buildPc(target.user_id);
            await attachLocalMedia(pc);
            const offer = await pc.createOffer();
            await pc.setLocalDescription(offer);
            sendSignal({
                type: 'call', to: target.user_id, call_id: callId, sdp: offer,
            });
        } catch (e) {
            toast.error('Nelze zahájit hovor: ' + e.message);
            endCall(true);
        }
    }, [callPhase, buildPc, endCall]);

    // ---- Incoming call handlers ----
    const handleSignal = async (msg) => {
        if (msg.type === 'hello') {
            setMe({ user_id: msg.user_id, ...msg.info });
            setOnline(msg.online || []);
            return;
        }
        if (msg.type === 'presence') { setOnline(msg.online || []); refreshDirectory(); return; }
        if (msg.type === 'unavailable') {
            toast.error('Volaný není online.');
            endCall(true);
            return;
        }
        if (msg.type === 'call') {
            if (callPhase !== 'idle') {
                sendSignal({ type: 'decline', to: msg.from, call_id: msg.call_id, reason: 'busy' });
                return;
            }
            callIdRef.current = msg.call_id;
            peerIdRef.current = msg.from;
            setPeer({ user_id: msg.from, ...(msg.from_info || {}) });
            setCallPhase('incoming');
            playRingtone('ring');
            const pc = buildPc(msg.from);
            await pc.setRemoteDescription(new RTCSessionDescription(msg.sdp));
            // Drain any ICE candidates that arrived first
            for (const c of pendingIceRef.current) await pc.addIceCandidate(c);
            pendingIceRef.current = [];
            sendSignal({ type: 'ringing', to: msg.from, call_id: msg.call_id });
            return;
        }
        if (msg.type === 'answer') {
            stopRingtones();
            const pc = pcRef.current;
            if (!pc) return;
            await pc.setRemoteDescription(new RTCSessionDescription(msg.sdp));
            for (const c of pendingIceRef.current) await pc.addIceCandidate(c);
            pendingIceRef.current = [];
            setCallPhase('in_call');
            startCallTimer();
            return;
        }
        if (msg.type === 'ice') {
            const pc = pcRef.current;
            try {
                if (pc && pc.remoteDescription) {
                    await pc.addIceCandidate(new RTCIceCandidate(msg.candidate));
                } else {
                    pendingIceRef.current.push(new RTCIceCandidate(msg.candidate));
                }
            } catch (_) {}
            return;
        }
        if (msg.type === 'bye' || msg.type === 'decline') {
            if (msg.type === 'decline') toast.warning('Volaný odmítl.');
            endCall(true);
            return;
        }
    };

    const acceptCall = useCallback(async () => {
        const pc = pcRef.current;
        if (!pc || callPhase !== 'incoming') return;
        try {
            await attachLocalMedia(pc);
            const answer = await pc.createAnswer();
            await pc.setLocalDescription(answer);
            sendSignal({
                type: 'answer', to: peerIdRef.current,
                call_id: callIdRef.current, sdp: answer,
            });
            stopRingtones();
            setCallPhase('in_call');
            startCallTimer();
        } catch (e) {
            toast.error('Nelze přijmout: ' + e.message);
            endCall();
        }
    }, [callPhase, endCall]);

    const rejectCall = useCallback(() => {
        sendSignal({
            type: 'decline', to: peerIdRef.current,
            call_id: callIdRef.current, reason: 'declined',
        });
        endCall(true);
    }, [endCall]);

    const toggleMute = () => {
        const stream = localStreamRef.current;
        if (!stream) return;
        const enable = muted;
        stream.getAudioTracks().forEach((t) => { t.enabled = enable; });
        setMuted(!muted);
    };

    const fmt = (s) => `${String(Math.floor(s/60)).padStart(2,'0')}:${String(s%60).padStart(2,'0')}`;

    const phaseColor =
        callPhase === 'in_call' ? 'var(--brand)' :
        callPhase === 'incoming' ? '#f59e0b' :
        callPhase === 'dialing' ? '#3b82f6' : 'var(--text-secondary)';

    return (
        <div data-testid="intercom-page">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-3xl font-heading font-bold flex items-center gap-3">
                        <Broadcast size={28} style={{ color: 'var(--brand)' }} />
                        Interkom
                    </h1>
                    <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
                        Interní WebRTC volání mezi přihlášenými uživateli CMS — žádný PBX, pouze váš server.
                    </p>
                </div>
                <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider"
                     data-testid="ic-ws-status">
                    {wsPhase === 'online' && (
                        <><CheckCircle size={16} weight="fill" color="#22c55e" />
                          <span style={{ color: '#22c55e' }}>Připojeno</span></>
                    )}
                    {wsPhase === 'connecting' && (
                        <><SpinnerGap size={16} className="animate-spin" /><span>Připojuji…</span></>
                    )}
                    {wsPhase === 'offline' && (
                        <><XCircle size={16} weight="fill" color="#ef4444" />
                          <span style={{ color: '#ef4444' }}>Odpojeno</span>
                          <button onClick={connectWs} data-testid="ic-reconnect"
                                  className="ml-2 px-2 py-1 rounded border text-xs"
                                  style={{ borderColor: 'var(--border-default)' }}>
                              <ArrowsClockwise size={12} className="inline mr-1" /> Reconnect
                          </button></>
                    )}
                </div>
            </div>

            {me && (
                <div className="rounded-xl border p-4 mb-4 flex items-center justify-between"
                     style={{ background: 'var(--bg-surface)', borderColor: 'var(--border-default)' }}>
                    <div className="flex items-center gap-3">
                        <UserCircle size={28} style={{ color: 'var(--brand)' }} />
                        <div>
                            <div className="font-bold" data-testid="ic-me-name">{me.name}</div>
                            <div className="text-xs font-mono" style={{ color: 'var(--text-secondary)' }}>
                                {me.email}{me.extension && <> · klapka {me.extension}</>}
                            </div>
                        </div>
                    </div>
                    <span className="text-[10px] uppercase tracking-[0.2em] px-3 py-1 rounded-full"
                          style={{ background: 'rgba(163,230,53,0.15)', color: 'var(--brand)' }}>
                        {online.length} online
                    </span>
                </div>
            )}

            {(callPhase !== 'idle') && (
                <div className="rounded-2xl border p-6 mb-4 text-center"
                     data-testid="ic-call-card"
                     style={{ background: 'var(--bg-surface)', borderColor: phaseColor }}>
                    <div className="text-xs uppercase tracking-[0.2em] mb-2"
                         style={{ color: phaseColor }}>
                        {PhaseLabel[callPhase]}
                    </div>
                    <div className="text-2xl font-bold mb-1" data-testid="ic-peer-name">
                        {peer?.name || 'Neznámý'}
                    </div>
                    <div className="text-xs font-mono mb-3" style={{ color: 'var(--text-secondary)' }}>
                        {peer?.email}{peer?.extension && <> · klapka {peer.extension}</>}
                    </div>
                    {callPhase === 'in_call' && (
                        <div className="text-3xl font-mono mb-4" data-testid="ic-call-duration"
                             style={{ color: 'var(--brand)' }}>
                            {fmt(duration)}
                        </div>
                    )}
                    <div className="flex justify-center gap-3">
                        {callPhase === 'incoming' && (
                            <>
                                <button onClick={acceptCall} data-testid="ic-accept-btn"
                                        className="px-6 py-3 rounded-full text-white font-bold flex items-center gap-2"
                                        style={{ background: 'var(--success, #22c55e)' }}>
                                    <Phone size={20} weight="fill" /> Přijmout
                                </button>
                                <button onClick={rejectCall} data-testid="ic-reject-btn"
                                        className="px-6 py-3 rounded-full text-white font-bold flex items-center gap-2"
                                        style={{ background: 'var(--danger, #ef4444)' }}>
                                    <PhoneSlash size={20} weight="fill" /> Odmítnout
                                </button>
                            </>
                        )}
                        {(callPhase === 'in_call' || callPhase === 'dialing') && (
                            <>
                                {callPhase === 'in_call' && (
                                    <button onClick={toggleMute} data-testid="ic-mute-btn"
                                            className="px-5 py-3 rounded-full border flex items-center gap-2"
                                            style={{ borderColor: muted ? 'var(--danger)' : 'var(--border-default)' }}>
                                        {muted ? <MicrophoneSlash size={18} color="var(--danger)"/>
                                               : <Microphone size={18}/>}
                                    </button>
                                )}
                                <button onClick={() => endCall()} data-testid="ic-hangup-btn"
                                        className="px-6 py-3 rounded-full text-white font-bold flex items-center gap-2"
                                        style={{ background: 'var(--danger, #ef4444)' }}>
                                    <PhoneSlash size={20} weight="fill" /> Zavěsit
                                </button>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* Directory */}
            <div className="rounded-xl border overflow-hidden"
                 style={{ background: 'var(--bg-surface)', borderColor: 'var(--border-default)' }}>
                <div className="px-5 py-3 border-b text-xs font-bold uppercase tracking-[0.15em]"
                     style={{ borderColor: 'var(--border-default)', color: 'var(--text-secondary)' }}>
                    Adresář uživatelů ({directory.length})
                </div>
                <table className="w-full text-sm" data-testid="ic-directory">
                    <thead>
                        <tr className="text-left text-[11px] uppercase tracking-wider"
                            style={{ color: 'var(--text-disabled)' }}>
                            <th className="px-5 py-2">Stav</th>
                            <th className="px-5 py-2">Jméno</th>
                            <th className="px-5 py-2">Email</th>
                            <th className="px-5 py-2">Klapka</th>
                            <th className="px-5 py-2"></th>
                        </tr>
                    </thead>
                    <tbody>
                    {directory.map((u) => {
                        const isMe = me && u.user_id === me.user_id;
                        const canCall = u.online && !isMe && callPhase === 'idle';
                        return (
                            <tr key={u.user_id} className="border-t"
                                style={{ borderColor: 'var(--border-default)' }}>
                                <td className="px-5 py-3">
                                    <span className="inline-flex items-center gap-1.5 text-xs font-bold">
                                        <span className="w-2 h-2 rounded-full"
                                              style={{ background: u.online ? '#22c55e' : '#64748b' }}/>
                                        <span style={{ color: u.online ? '#22c55e' : 'var(--text-disabled)' }}>
                                            {u.online ? 'ONLINE' : 'OFFLINE'}
                                        </span>
                                    </span>
                                </td>
                                <td className="px-5 py-3">{u.name}{isMe && <span className="ml-2 text-xs"
                                    style={{ color: 'var(--brand)' }}>(vy)</span>}</td>
                                <td className="px-5 py-3 font-mono text-xs">{u.email}</td>
                                <td className="px-5 py-3 font-mono">{u.extension || '—'}</td>
                                <td className="px-5 py-3 text-right">
                                    <button
                                        data-testid={`ic-call-${u.user_id}`}
                                        onClick={() => placeCall(u)}
                                        disabled={!canCall}
                                        className="px-4 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1.5
                                                  disabled:opacity-30 disabled:cursor-not-allowed inline-flex"
                                        style={{ background: canCall ? 'var(--brand)' : 'transparent',
                                                 color: canCall ? '#000' : 'var(--text-disabled)',
                                                 border: canCall ? 'none' : '1px solid var(--border-default)' }}
                                    >
                                        <Phone size={14} weight="fill" /> Volat
                                    </button>
                                </td>
                            </tr>
                        );
                    })}
                    {directory.length === 0 && (
                        <tr><td colSpan={5} className="px-5 py-8 text-center text-sm"
                                style={{ color: 'var(--text-disabled)' }}>
                            Načítám uživatele…
                        </td></tr>
                    )}
                    </tbody>
                </table>
            </div>

            <audio ref={remoteAudioRef} autoPlay data-testid="ic-remote-audio" />
            <audio ref={ringtoneRef} loop preload="auto"
                   src="data:audio/wav;base64,UklGRjQAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YRAAAACA////fwAAgP///38AAID//w=="/>
            <audio ref={dialtoneRef} loop preload="auto"
                   src="data:audio/wav;base64,UklGRjQAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YRAAAACA////fwAAgP///38AAID//w=="/>
        </div>
    );
};

export default Intercom;
